import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import { ArticleDetail } from "@sellernote/shared/src/api/dto/entities/common/support";
import { ShipdaCurrentLanguage } from "@sellernote/shared/src/i18n/i18nForShipda";
import {
  getArticleBoardTypeString,
  getBoardHref,
} from "@sellernote/features/src/common/utils/support";
import {
  supportActions,
  supportSelectors,
} from "@sellernote/shipda-kr/src/modules/support";
import { useAppSelector } from "@sellernote/shipda-kr/src/store";
import BoardContent from "@sellernote/ui/src/sds-v1/components/board/BoardContent";
import BoardPageTitle from "@sellernote/ui/src/sds-v1/components/board/BoardPageTitle";
import BoardPrevNextItem from "@sellernote/ui/src/sds-v1/components/board/BoardPrevNextItem";
import Loading from "@sellernote/ui/src/sds-v1/components/Loading";

import CommonStyled from "../../pages/support/index.styles";
import SupportBreadcrumb from "../../pages/support/SupportBreadcrumb";
import SupportLayout from "../../pages/support/SupportLayout";

import Seo from "../Seo";
import CategoryList from "./CategoryList";
import GuideCategoryList from "./GuideCategoryList";

function getSeoContents({
  articleType,
}: {
  articleType: ArticleDetail["articleType"] | undefined;
}) {
  const seoContents: {
    title: string;
    description: string;
  } = {
    title: "",
    description: "",
  };

  switch (articleType) {
    case "howto": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "이용방법 - 디지털 포워딩 서비스 쉽다"
          : "Guide - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "디지털 포워딩 쉽다를 100% 활용해 해상 및 항공수출입을 더 쉽게 시작하세요."
          : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services.";
      break;
    }
    case "terms": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "무역용어 및 운송프로세스 - 디지털 포워딩 서비스 쉽다"
          : "Terms & Process - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "디지털 포워더 쉽다를 이용할 때 꼭 필요한 무역용어와 수출입 프로세스를 알려드립니다."
          : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services.";
      break;
    }
    case "faq": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "FAQ - 디지털 포워딩 서비스 쉽다"
          : "FAQ - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "디지털 포워더 쉽다에게 주로 문의하는 질문을 알려드립니다."
          : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services.";
      break;
    }
    case "notice": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "공지사항 - 수출입 디지털 운송 서비스 쉽다"
          : "Notice - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "쉽다 디지털물류 서비스에서 수출입 무역을 진행하는 고객사분들께 주요사항을 공지합니다."
          : "Notice for ShipDa customers to announce important information for our export/import customers about ocean FCL/LCL and AIR freight forwarding services.";
      break;
    }
    case "video": {
      seoContents.title =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "소개 및 영상 - 디지털 포워딩 서비스 쉽다"
          : "Introduction and Video - ShipDa - Digital freight forwarding service";
      seoContents.description =
        ShipdaCurrentLanguage.currentLanguage === "ko"
          ? "수출, 수입물류를 전문적으로 도와줄 포워딩 회사를 찾는다면, 디지털 포워딩 쉽다와 쉽게 시작하세요."
          : "Service introductions and video for ShipDa customers, offering comprehensive insights into ShipDa's services";
      break;
    }
  }

  return seoContents;
}

export default function SupportBoardContent() {
  const { t } = useTranslation();

  const router = useRouter();
  const dispatch = useDispatch();

  const {
    pathname,
    query: { id },
  } = router;

  const { supportContents } = useAppSelector((state) => {
    return {
      supportContents: supportSelectors.getSupportContents(state),
    };
  });

  useEffect(() => {
    return () => {
      dispatch(supportActions.INIT_GET_DETAIL({}));
    };
  }, []);

  const seoContents = useMemo(
    () =>
      getSeoContents({
        articleType: supportContents?.targetArticle?.articleType,
      }),
    [supportContents?.targetArticle?.articleType]
  );

  if (!supportContents)
    return (
      <Loading active={!supportContents} label={t("support-new:공통_로딩")} />
    );

  const { title, contents, memo, videoURL, articleType, translatedBy } =
    supportContents.targetArticle;

  return (
    <SupportLayout>
      <Seo
        path={`${getBoardHref(articleType)}/${id}`}
        title={seoContents.title}
        description={seoContents.description}
      />

      <CommonStyled.mainContainer>
        <SupportBreadcrumb title={title} />

        <BoardPageTitle titleStyle="default">
          {getArticleBoardTypeString(articleType)}
        </BoardPageTitle>

        <BoardContent
          title={title}
          annotation={memo}
          desc={contents}
          videoURL={videoURL}
          translatedBy={translatedBy}
        />

        <BoardPrevNextItem
          prevData={supportContents.beforeArticle}
          nextData={supportContents.nextArticle}
          boardHref={pathname.replace("[id]", "")}
        />
      </CommonStyled.mainContainer>

      {articleType === "terms" ||
      articleType === "howto" ||
      articleType === "faq" ? (
        <GuideCategoryList />
      ) : (
        <CategoryList articleType={articleType} />
      )}
    </SupportLayout>
  );
}
